import { CarouselItemContent } from '../../template/imageCarouselTypes';
import { ListItem, buildAddBelowItem, parseBulletLines } from '../../template/leftRightListUtils';
import type { TableRow } from '../../template/Table';
import { ContentHeadingProps, ContentProps } from '../../../models/TopLevelTemplateComponentProps';
import { headingNotSet, newContentId, newTextContent } from '../../../services/TemplateUtils';
import { delimitedToRows } from './tableImport';
import { cellFromText, cellText, normalizeTable } from './tableModel';

/**
 * Pure "common structure to assume" heuristics for a plain-text paste, one per
 * block type, plus the shared helpers `parsePastedContent` uses to turn a
 * detected table grid into a non-Table block's shape. No React, no clipboard.
 *
 * Each heuristic reports a `confidence`: `high` when the text clearly carried
 * structure (a list, `Label: value` lines, delimited rows) so the result can be
 * applied silently; `low` when it was an unstructured blob that the user may
 * want to hand to AI.
 */

export type Confidence = 'high' | 'low';

const LIST_MARKER = /^\s*([-–—•*]|\d+[.)])\s+/;
const LIST_MARKER_HEAD = /^([-–—•*]|\d+[.)])\s+/;
const INDENT = /^(\t| {2,})/;
const MULTI_SPACE = /\s{2,}/;
const HEADING_MAX_LEN = 80;
const TERMINATOR = /[.?!:,]$/;
const LABEL_MATCH_RATIO = 0.6;
const LIST_LINE_RATIO = 0.6;

/** HTML-escape a plain-text fragment so it can be stored as inline content. */
export function escapeInline(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Strip a leading `-`, `*`, `•` or `1.` list marker from a line. */
export function stripListMarker(line: string): string {
  return line.trim().replace(LIST_MARKER_HEAD, '');
}

function nonBlankLines(text: string): string[] {
  return (text ?? '')
    .replace(/\r/g, '')
    .split('\n')
    .filter((line) => line.trim().length > 0);
}

/**
 * True when the text is almost certainly delimited rows: any line has a tab, or
 * every line has the same (>=1) number of commas. Prose with the odd comma fails.
 */
export function isDelimited(text: string): boolean {
  const lines = nonBlankLines(text);
  if (lines.length < 1) return false;
  if (lines.some((line) => line.includes('\t'))) return true;
  if (lines.length < 2) return false;
  const counts = lines.map((line) => (line.match(/,/g) ?? []).length);
  return counts[0] >= 1 && counts.every((count) => count === counts[0]);
}

// ---------------------------------------------------------------------------
// Text Section
// ---------------------------------------------------------------------------

function splitParagraphs(text: string): string[] {
  return (text ?? '')
    .replace(/\r/g, '')
    .split(/\n\s*\n/)
    .map((para) => para.trim())
    .filter(Boolean);
}

function looksLikeList(lines: string[]): boolean {
  if (lines.length === 0) return false;
  const marked = lines.filter((line) => LIST_MARKER.test(line)).length;
  // A lone marked line (a sentence that happens to start with "– " / "* " / "1. ")
  // is prose, not a list.
  if (marked < 2) return false;
  return marked / lines.length >= LIST_LINE_RATIO;
}

type NestedContent = ([ContentProps, ContentProps[]] | ContentProps)[];

function buildNestedList(lines: string[]): NestedContent {
  const out: NestedContent = [];
  lines.forEach((raw) => {
    const label = escapeInline(stripListMarker(raw));
    if (!label) return;
    if (INDENT.test(raw) && out.length > 0) {
      const last = out[out.length - 1];
      const entry: [ContentProps, ContentProps[]] = Array.isArray(last) ? last : [last, []];
      out[out.length - 1] = entry;
      entry[1].push(newTextContent(label));
    } else {
      out.push(newTextContent(label));
    }
  });
  return out;
}

export interface StandardHeaderHeuristic {
  content: ContentProps | ContentProps[] | NestedContent;
  heading?: string;
  confidence: Confidence;
}

/** Blank lines → paragraphs; mostly-bulleted → a (optionally nested) list. */
export function plainTextToStandardHeaderContent(
  text: string,
  headingIsEmpty: boolean,
): StandardHeaderHeuristic {
  const paragraphs = splitParagraphs(text);
  const lines = paragraphs.flatMap((para) => para.split('\n')).filter((line) => line.trim());
  if (looksLikeList(lines)) {
    return { content: buildNestedList(lines), confidence: 'high' };
  }

  const body = [...paragraphs];
  let heading: string | undefined;
  if (
    headingIsEmpty &&
    body.length > 1 &&
    !body[0].includes('\n') &&
    body[0].length <= HEADING_MAX_LEN &&
    !TERMINATOR.test(body[0])
  ) {
    heading = body.shift();
  }

  // Plain prose stays ONE node (paragraphs joined by <br><br>). An array renders
  // as a <ul>, so multiple paragraph nodes would show as an unwanted bullet list.
  const bodyParas = body.map((para) => escapeInline(para.replace(/\n/g, ' '))).filter(Boolean);
  const content = newTextContent(bodyParas.join('<br><br>'));
  return { heading, content, confidence: body.length >= 2 ? 'high' : 'low' };
}

// ---------------------------------------------------------------------------
// Key Details List
// ---------------------------------------------------------------------------

const SEPARATORS: ReadonlyArray<{ sep: string; len: number }> = [
  { sep: '\t', len: 1 },
  { sep: ' — ', len: 3 },
  { sep: ' – ', len: 3 },
  { sep: ': ', len: 2 },
];

function splitLabelValue(line: string): { label: string; value: string; matched: boolean } {
  for (const { sep, len } of SEPARATORS) {
    const idx = line.indexOf(sep);
    if (idx > 0) {
      return {
        label: line.slice(0, idx).trim(),
        value: line.slice(idx + len).trim(),
        matched: true,
      };
    }
  }
  return { label: line.trim(), value: '', matched: false };
}

function keyDetailItem(label: string, value: string): ListItem {
  if (!value) return { heading: newTextContent(label), text: undefined };
  return buildAddBelowItem(label, value);
}

function lastItemHasValue(items: ListItem[]): boolean {
  const text = items[items.length - 1]?.text;
  if (!text) return false;
  return Array.isArray(text) ? text.length > 0 : text.content.trim().length > 0;
}

function appendToLastValue(items: ListItem[], extra: string): void {
  const item = items[items.length - 1];
  const parts = Array.isArray(item.text)
    ? item.text.map((entry) => entry.content)
    : [(item.text as ContentProps).content];
  parts.push(extra);
  item.text =
    parts.length > 1 ? parts.map((part) => newTextContent(part)) : newTextContent(parts[0]);
}

export interface LeftRightHeuristic {
  items: ListItem[];
  confidence: Confidence;
}

/** One item per line; `Label: value` / `Label\tvalue` / `Label — value` split. */
export function plainTextToLeftRightItems(text: string): LeftRightHeuristic {
  const lines = nonBlankLines(text).map((line) => line.replace(/\s+$/, ''));
  const items: ListItem[] = [];
  let matched = 0;
  lines.forEach((line) => {
    const parsed = splitLabelValue(line);
    if (parsed.matched) matched += 1;
    if (!parsed.matched && items.length > 0 && lastItemHasValue(items)) {
      appendToLastValue(items, line.trim());
      return;
    }
    items.push(keyDetailItem(parsed.label, parsed.value));
  });
  const confidence: Confidence =
    lines.length > 0 && matched / lines.length >= LABEL_MATCH_RATIO ? 'high' : 'low';
  return { items, confidence };
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export interface TableHeuristic {
  rows: TableRow[];
  confidence: Confidence;
}

/** Delimited → real rows; fixed-width columns → split on runs of spaces; else 1 column. */
export function plainTextToTableRows(text: string): TableHeuristic {
  if (isDelimited(text)) return { rows: delimitedToRows(text), confidence: 'high' };
  const lines = nonBlankLines(text);
  if (lines.length > 0 && lines.every((line) => MULTI_SPACE.test(line))) {
    return {
      rows: normalizeTable(
        lines.map((line) => ({ cells: line.split(MULTI_SPACE).map(cellFromText) })),
      ),
      confidence: 'high',
    };
  }
  return {
    rows: normalizeTable(lines.map((line) => ({ cells: [cellFromText(line)] }))),
    confidence: 'low',
  };
}

// ---------------------------------------------------------------------------
// Image Cards
// ---------------------------------------------------------------------------

export interface CarouselHeuristic {
  items: CarouselItemContent[];
}

/** Plain text can't make an image — fill the last slide's caption instead. */
export function plainTextToCarouselCaption(
  text: string,
  properties: { items: CarouselItemContent[] },
): CarouselHeuristic | { empty: string } {
  if (!properties.items || properties.items.length === 0) {
    return { empty: 'Add a slide image first, then paste its caption.' };
  }
  const lines = (text ?? '').split(/\r?\n/);
  const firstIdx = lines.findIndex((line) => line.trim());
  const firstLine = firstIdx >= 0 ? lines[firstIdx].trim() : '';
  const rest = lines
    .slice(firstIdx + 1)
    .join('\n')
    .trim();
  const lastIndex = properties.items.length - 1;

  const items = properties.items.map((item, index) => {
    if (index !== lastIndex) return item;
    const headingEmpty = headingNotSet(item.heading as ContentHeadingProps);
    return {
      ...item,
      heading:
        headingEmpty && firstLine
          ? { id: item.heading?.id ?? newContentId(), content: firstLine }
          : item.heading,
      text: rest
        ? {
            id: item.text?.id ?? newContentId(),
            content: escapeInline(rest).replace(/\n/g, '<br>'),
          }
        : item.text,
    };
  });
  return { items };
}

// ---------------------------------------------------------------------------
// Detected table grid → non-Table block shape (mirrors processBulletsWithTableMarkdown)
// ---------------------------------------------------------------------------

/** Text of every cell in a parsed table, row by row. */
export function gridToTextRows(rows: TableRow[]): string[][] {
  return rows.map((row) => row.cells.map((cell) => cellText(cell).trim()));
}

/** Each row's cells joined into one bullet — a Text Section list. */
export function gridToStandardHeaderContent(grid: string[][]): ContentProps[] {
  return grid
    .map((row) => row.filter(Boolean).join(' ').trim())
    .filter(Boolean)
    .map((line) => newTextContent(line));
}

/** First cell → label, the rest joined → value. */
export function gridToLeftRightItems(grid: string[][]): ListItem[] {
  return grid.map((row) => {
    const value = row.slice(1).join(' ').trim();
    return {
      heading: newTextContent(row[0] ?? ''),
      text: value ? newTextContent(value) : undefined,
    };
  });
}

/** Bullet lines from a rich-HTML `<li>`/`<p>` walk, reusing the list-marker strip. */
export function htmlLinesToBullets(lines: string[]): ContentProps[] {
  return lines
    .flatMap((line) => parseBulletLines(line))
    .filter(Boolean)
    .map((line) => newTextContent(escapeInline(line)));
}
